import { useMemo } from 'react';

import { AddIcon, Button, Divider, ExcelIcon, Select, type TypeSelectOption } from '@thefarmersfront/kitchen';

import { Table, TableBottomField, TableTopField } from '~/components/common';

import { useStableCallback } from '~/hooks/common';
import { getDeliveryAgencyTrackingsColumnDefs } from '~/utils/delivery-agency/table';

import {
  DELIVERY_AGENCY_CREATED_SORT_OPTIONS,
  DELIVERY_AGENCY_TRACKING_PAGE_SIZE,
  type TypeDeliveryAgencySortBy,
} from '~/constants/delivery-agency';

import type { TypePagination } from '~/types/common.types';
import type { TypeDeliveryAgencyShippingLabel } from '~/types/delivery-agency.types';

import styles from './DeliveryAgencyTrackingTable.module.scss';

const getShippingLabelRowId = (row: TypeDeliveryAgencyShippingLabel) => String(row.shippingLabelId);

const DeliveryAgencyTrackingTable = ({
  data,
  pagination,
  sortBy,
  selectedItems,
  isDownloadReady,
  onChangePageLimit,
  onChangeSort,
  onChangePage,
  onChangeRowSelection,
  onClickViewDetail,
  onClickPrintShippingLabel,
  onClickTrackingExcelDownload,
  onClickCancelDelivery,
  onClickCreateShippingLabel,
}: {
  data: TypeDeliveryAgencyShippingLabel[] | null;
  pagination: TypePagination;
  sortBy: TypeDeliveryAgencySortBy;
  selectedItems: TypeDeliveryAgencyShippingLabel[];
  isDownloadReady: boolean;
  onChangePageLimit: (value: TypeSelectOption<number>) => void;
  onChangeSort: (value: TypeDeliveryAgencySortBy) => void;
  onChangePage: (value: number) => void;
  onChangeRowSelection: (selectedRows: TypeDeliveryAgencyShippingLabel[]) => void;
  onClickViewDetail: (order: TypeDeliveryAgencyShippingLabel) => void;
  onClickPrintShippingLabel: () => void;
  onClickTrackingExcelDownload: () => void;
  onClickCancelDelivery: () => void;
  onClickCreateShippingLabel: () => void;
}) => {
  const handleClickViewDetail = useStableCallback(onClickViewDetail);

  const { columnDefs } = useMemo(
    () =>
      getDeliveryAgencyTrackingsColumnDefs({
        currentPage: pagination.currentPage - 1,
        pageSize: pagination.pageSize,
        onClickViewDetail: handleClickViewDetail,
      }),
    [pagination.currentPage, pagination.pageSize, handleClickViewDetail]
  );

  // 선택은 페이지의 selectedItems 가 정한다. 출력·취소 뒤 페이지가 비우면 체크도 함께 풀린다.
  const selectedRowIds = useMemo(() => selectedItems.map(getShippingLabelRowId), [selectedItems]);

  return (
    <div className={styles['table-container']}>
      <TableTopField
        dataTotalCount={pagination.totalCount}
        rightButton={
          <div className={styles['table-top-right-field']}>
            <Button
              size="tiny"
              variant="outlined"
              color="tertiary"
              disabled={selectedItems.length === 0}
              onClick={onClickPrintShippingLabel}
            >
              운송장 출력
            </Button>
            <Divider className={styles['divider']} />
            <Button
              size="tiny"
              variant="outlined"
              color="tertiary"
              disabled={!isDownloadReady}
              leftIcon={<ExcelIcon size={16} />}
              onClick={onClickTrackingExcelDownload}
            >
              운송장 정보 전체 다운로드
            </Button>
            <Divider className={styles['divider']} />
            <Button
              size="tiny"
              variant="outlined"
              status="error"
              disabled={selectedItems.length === 0}
              onClick={onClickCancelDelivery}
            >
              배송 취소
            </Button>
            <Divider className={styles['divider']} />
            <Button size="tiny" color="secondary" leftIcon={<AddIcon size={16} />} onClick={onClickCreateShippingLabel}>
              추가 운송장 생성
            </Button>
          </div>
        }
      />
      <div className={styles['table-wrapper']}>
        <Table
          data={data}
          columns={columnDefs}
          getRowId={getShippingLabelRowId}
          rowStatusType={data === null ? 'INIT' : 'EMPTY'}
          selection={{ selectedRowIds, onChange: onChangeRowSelection }}
        />
      </div>
      <TableBottomField
        pageSizeOptions={Object.values(DELIVERY_AGENCY_TRACKING_PAGE_SIZE)}
        currentPage={pagination.currentPage - 1}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onChangePageLimit={onChangePageLimit}
        onClickPage={(page) => onChangePage(page + 1)}
        leftField={
          <Select
            maxWidth="160px"
            size="tiny"
            dropdownPlacement="top-left"
            options={DELIVERY_AGENCY_CREATED_SORT_OPTIONS}
            value={DELIVERY_AGENCY_CREATED_SORT_OPTIONS.find((option) => option.value === sortBy)}
            onChange={(_: unknown, option: TypeSelectOption<TypeDeliveryAgencySortBy>) => onChangeSort(option.value)}
          />
        }
      />
    </div>
  );
};

export default DeliveryAgencyTrackingTable;
